/**
 * IndexCapacityHeatmapGrid.tsx
 * Item 5.3 → P3 rework (2026-07-29, qa/7-28-2026/DEV-PLAN-gigi-answers.md):
 * the day-column header + ONE ROW PER INDEX TEST TYPE ("each index test is
 * treated like a machine" — Mike). Each row's cells are that type's own % of
 * its own tests/day capacity; the old single summed "Index Machine" row is
 * retired. Rows + header scroll horizontally together (the label column stays
 * pinned via flex, not scroll).
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import {
  IndexCapacityHeatmapCell,
  HEATMAP_CELL_WIDTH,
  HEATMAP_CELL_HEIGHT,
} from './IndexCapacityHeatmapCell';
import type { IndexTypeRow } from './indexHeatmapModel';
import type { DayBaselineGhost } from './indexBaselineDiff';
import { typeGhostKey } from './indexBaselineDiff';

const LABEL_WIDTH = 150;
const HEADER_HEIGHT = 34;

/** {weekday abbreviation, "M/D"} for a day-header cell, UTC-safe (ISO dates carry no timezone). */
function dayLabelParts(iso: string): { weekday: string; monthDay: string } {
  const d = new Date(`${iso}T00:00:00Z`);
  const weekday = d.toLocaleDateString('en-CA', { weekday: 'short', timeZone: 'UTC' });
  const monthDay = d.toLocaleDateString('en-CA', {
    month: 'numeric',
    day: 'numeric',
    timeZone: 'UTC',
  });
  return { weekday, monthDay };
}

export interface IndexCapacityHeatmapGridProps {
  rows: readonly IndexTypeRow[];
  /** P3 per-type ghosts keyed by `typeGhostKey(taskTypeKey, date)`, or undefined when compare mode is off. */
  baselineGhosts?: ReadonlyMap<string, DayBaselineGhost>;
}

export const IndexCapacityHeatmapGrid: React.FC<IndexCapacityHeatmapGridProps> = ({
  rows,
  baselineGhosts,
}) => {
  const theme = useTheme();
  const headerDays = rows[0]?.cells ?? [];

  return (
    <Box
      sx={{
        display: 'flex',
        border: `1px solid ${theme.border.color}`,
        borderRadius: `${theme.border.radius}px`,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {/* Pinned label column — one label per test type, outside the scroll container. */}
      <Box sx={{ width: LABEL_WIDTH, flexShrink: 0, borderRight: `2px solid ${theme.border.color}` }}>
        <Box
          sx={{
            height: HEADER_HEIGHT,
            backgroundColor: theme.table.headerBg,
            borderBottom: `1px solid ${theme.border.color}`,
          }}
        />
        {rows.map((row) => (
          <Box
            key={row.taskTypeKey}
            sx={{
              height: HEATMAP_CELL_HEIGHT,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              px: 1.5,
              borderBottom: `1px solid ${alpha(theme.border.color, 0.5)}`,
            }}
          >
            <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: '0.74rem', maxWidth: LABEL_WIDTH - 24 }}>
              {row.taskTypeName}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
              {row.capacity != null ? `cap ${row.capacity}/day` : 'cap 8/day (default)'}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Header row + one data row per type scroll TOGETHER (one shared overflow-x container). */}
      <Box sx={{ overflowX: 'auto', flex: 1 }}>
        <Box
          role="row"
          sx={{
            display: 'flex',
            height: HEADER_HEIGHT,
            backgroundColor: theme.table.headerBg,
            borderBottom: `1px solid ${theme.border.color}`,
          }}
        >
          {headerDays.map((m) => {
            const { weekday, monthDay } = dayLabelParts(m.date);
            return (
              <Box
                key={m.date}
                sx={{
                  width: HEATMAP_CELL_WIDTH,
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: `1px solid ${theme.border.color}`,
                  backgroundColor: m.isWorkingDay
                    ? 'transparent'
                    : alpha(theme.schedule.noActivity, 0.12),
                }}
              >
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, color: 'text.secondary', lineHeight: 1.1 }}>
                  {weekday}
                </Typography>
                <Typography sx={{ fontSize: '0.58rem', color: 'text.secondary', lineHeight: 1.1 }}>
                  {monthDay}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {rows.map((row) => (
          /* No borderBottom here — the cells carry the row separator INSIDE their
             border-box height. A border on this wrapper made each data row 1px
             taller than its pinned label row, so the two panes drifted apart
             progressively down the grid (alignment bug, 2026-08-04). */
          <Box key={row.taskTypeKey} role="row" sx={{ display: 'flex' }}>
            {row.cells.map((m) => (
              <IndexCapacityHeatmapCell
                key={m.date}
                model={m}
                baselineGhost={baselineGhosts?.get(typeGhostKey(row.taskTypeKey, m.date)) ?? null}
              />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
